import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import {
  withMultiSelectCombobox,
  useFluentV1Styles,
  useMultiSelectContext,
} from '@bingads-webui-campaign-react/multi-selector';
import { useDebouncedState } from '@bingads-webui-campaign-react/common-generic-hooks';
import {
  FluentProvider,
  webLightTheme,
  makeStyles,
  mergeClasses,
  shorthands,
  useId,
  Combobox,
  Option,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LoadingOverlay } from '@bingads-webui-campaign-react/loading';

const MultiSelectCombobox = withMultiSelectCombobox(Combobox);
const searchStartDelayMs = 500;

const useMultiSelectorStyles = makeStyles({
  listbox: {
    maxHeight: '245px',
  },
});

const useStyles = makeStyles({
  ExistingBrandListsPicker: {
    width: '560px',
  },
  ExistingBrandListsPickerContent: {
    width: '552px',
  },
  BrandListItem: {
    width: '100%',
    paddingLeft: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    '&:hover': {
      backgroundColor: '#f4f4f4',
      cursor: 'pointer',
    },
  },
  BrandListItemName: {
    maxWidth: '435px',
    overflowWrap: 'break-word',
  },
  BrandListItemBrandsCount: {
    color: '#605e5c',
    float: 'right',
    alignSelf: 'center',
  },
  BrandListItemLink: {
    color: '#0078d4',
    ...shorthands.textDecoration('underline'),
  },
  NonOptionContent: {
    ...shorthands.margin('12px'),
    '& .brand-list-empty-header': {
      fontSize: '14px',
      lineHeight: '20px',
    },
    '& .brand-list-empty-description': {
      fontSize: '12px',
      lineHeight: '20px',
    },
  },
});

export const ExistingBrandListsPicker = ({
  disabled,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useStyles();

  const comboboxId = useId('combobox');
  const styles = useMultiSelectorStyles();
  const fluentV1ComboboxStyles = useFluentV1Styles();

  const {
    isLoadingOptions,
    isFirstOptionsLoad,
    isNoOptionsAfterFirstLoad,
    options,
    optionsLoadError,

    isLoadingSelectedOptions,
    isSelectedOptionsLimitReached,
    selectedOptions,
    setSelectedOptions,
    selectedOptionsLoadError,

    setSearchFilter: setSearchFilterFromContext,
  } = useMultiSelectContext();

  const hasLoadErrors = optionsLoadError || selectedOptionsLoadError;
  const showInitialLoading = (isFirstOptionsLoad && !hasLoadErrors) || isLoadingSelectedOptions;
  const hasInitialLoadErrors = isFirstOptionsLoad && hasLoadErrors;
  const canUserStartOperating = !showInitialLoading && !isNoOptionsAfterFirstLoad && !(hasInitialLoadErrors || selectedOptionsLoadError);
  const selectedOptionKeys = useMemo(() => _.pluck(selectedOptions, 'key'), [selectedOptions]);
  const selectedOptionsSet = useMemo(() => new Set(selectedOptionKeys), [selectedOptionKeys]);
  const shouldDisableSelection = useCallback(option => isSelectedOptionsLimitReached && !selectedOptionsSet.has(option.key), [isSelectedOptionsLimitReached, selectedOptionsSet]);

  const [isOpen, setIsOpen] = useState(false);
  const onOpenChange = useCallback((event, data) => {
    setIsOpen(data.open);
  }, []);

  const onOptionSelect = useCallback((event, data) => {
    setSelectedOptions(data.resolvedSelectedOptions);
  }, [setSelectedOptions]);

  const [debouncedSearchFilter, setSearchFilter, searchFilter] = useDebouncedState('', searchStartDelayMs);
  useEffect(() => {
    setSearchFilterFromContext(debouncedSearchFilter);
  }, [debouncedSearchFilter, setSearchFilterFromContext]);
  const onChange = useCallback((event) => {
    const { value: rawValue } = event.target;
    setSearchFilter(rawValue);
  }, [setSearchFilter]);

  return (
    <FluentProvider theme={webLightTheme}>
      <MultiSelectCombobox
        id={comboboxId}
        placeholder={i18n.getString(_TL_('Use existing brand lists'))}
        listbox={{ className: styles.listbox }}
        root={{ className: mergeClasses(fluentV1ComboboxStyles.root, classes.ExistingBrandListsPicker, 'existing-brand-lists-picker') }}
        disabled={disabled || !canUserStartOperating}
        onOptionSelect={onOptionSelect}
        value={searchFilter}
        onChange={onChange}
        options={options}
        selectedOptions={selectedOptions}
        open={isOpen}
        onOpenChange={onOpenChange}
      >
        <LoadingOverlay className={classes.ExistingBrandListsPickerContent} isVisible={isLoadingOptions} flexDirection="column">
          {!optionsLoadError &&
            <>
              {_.isEmpty(options) && (
                <div className={classNames(classes.NonOptionContent, 'brand-list-empty')}>
                  <div className="brand-list-empty-header">
                    {i18n.getString(_TL_('No brand list found'))}
                  </div>
                </div>
              )}
              {!_.isEmpty(options) && options.map(brand => (
                <Option key={brand.Id} value={brand.Id} text={brand.Name} disabled={isLoadingOptions || shouldDisableSelection(brand)}>
                  <div className={classNames(classes.BrandListItem, 'brand-list-item')}>
                    <span className={classNames(classes.BrandListItemName, 'brand-list-item-name')}>
                      {brand.Name}
                    </span>
                    <span className={classNames(classes.BrandListItemBrandsCount, 'brand-list-item-brands-count')}>
                      {brand.Count} {i18n.getString(_TL_('brands'))}
                    </span>
                  </div>
                </Option>
              ))}
            </>
          }
          {optionsLoadError &&
            <div className={classNames(classes.NonOptionContent, 'brand-list-load-error')}>
              {i18n.getString(_TL_('We cannot load the brand lists at this time. Please try again later.'))}
            </div>
          }
        </LoadingOverlay>
      </MultiSelectCombobox>
    </FluentProvider>
  );
};

ExistingBrandListsPicker.propTypes = {
  disabled: PropTypes.bool,
};

ExistingBrandListsPicker.defaultProps = {
  disabled: false,
};
